import { print } from 'graphql/language';
import { createBundle, mergeBundle, createDocument } from './bundle.js';
import { requestError, parseError, queryError, missingVariableError } from './error.js';

var resolved = new Promise(function (resolve) {
  return resolve(undefined);
});

var setVariable = function setVariable(variables, parameters, key, newName) {
  if (typeof parameters !== "object" || !parameters || !Object.prototype.hasOwnProperty.call(parameters, key)) {
    throw missingVariableError(key);
  }

  variables[newName] = parameters[key];
};

var createGroup = function createGroup(bundle, parameters, resolve, reject) {
  var variables = {};
  var firstBundleFields = {};
  var fieldMap = [firstBundleFields];
  bundle.variables.forEach(function (_, k) {
    return setVariable(variables, parameters, k, k);
  });
  bundle.fields.forEach(function (_, k) {
    firstBundleFields[k] = k;
  });
  return {
    bundle: bundle,
    variables: variables,
    fieldMap: fieldMap,
    promises: [{
      resolve: resolve,
      reject: reject
    }]
  };
};
/**
 * Handles a fetch-Response and parses it into a GraphQLResponse,
 * throws if the request is not ok or if JSON fails to parse.
 */


var handleFetchResponse = function handleFetchResponse(response) {
  return response.text().then(function (bodyText) {
    if (!response.ok) {
      throw requestError(response, bodyText, "Received status code " + response.status);
    }

    try {
      // Since it is successful we assume we have GraphQL-data
      return JSON.parse(bodyText);
    } catch (error) {
      throw parseError(response, bodyText, error);
    }
  });
};
var enqueue = function enqueue(pending, newBundle, parameters, resolve, reject) {
  var last = pending[pending.length - 1];

  if (last && last.bundle.operation === newBundle.operation) {
    var _mergeBundle = mergeBundle(last.bundle, newBundle),
        bundle = _mergeBundle.bundle,
        renamedVariables = _mergeBundle.renamedVariables,
        renamedFields = _mergeBundle.renamedFields;

    last.bundle = bundle;
    /* eslint-disable guard-for-in */

    for (var k in renamedVariables) {
      setVariable(last.variables, parameters, k, renamedVariables[k]);
    }
    /* eslint-enable guard-for-in */


    last.fieldMap.push(renamedFields);
    last.promises.push({
      resolve: resolve,
      reject: reject
    });
  } else {
    pending.push(createGroup(newBundle, parameters, resolve, reject));
  }
};
/**
 * Matches errors into groups based on their path.
 *
 * If an error is not in any of the supplied groups then it is assigned to all
 * of them.
 */

var groupErrors = function groupErrors(errors, groups) {
  var matchedErrors = groups.map(function (group) {
    return errors.filter(function (error) {
      return group.includes((error.path || [])[0]);
    });
  }); // We need to make sure that errors without a path also gets propagated

  var missingErrors = errors.filter(function (error) {
    return !matchedErrors.some(function (g) {
      return g.includes(error);
    });
  });
  matchedErrors.forEach(function (m) {
    return m.push.apply(m, missingErrors);
  });
  return matchedErrors;
};
var runGroup = function runGroup(runQuery, _ref) {
  var bundle = _ref.bundle,
      variables = _ref.variables,
      fieldMap = _ref.fieldMap,
      promises = _ref.promises;
  return runQuery({
    query: print(createDocument(bundle)),
    variables: variables
  }).then(function (bundledResponse) {
    var errors = groupErrors(bundledResponse.errors || [], fieldMap.map(function (map) {
      return Object.values(map);
    }));
    fieldMap.forEach(function (fields, i) {
      var data = {};

      if (typeof bundledResponse.data === "object" && bundledResponse.data) {
        /* eslint-disable guard-for-in */
        for (var k in fields) {
          data[k] = bundledResponse.data[fields[k]];
        }
        /* eslint-enable guard-for-in */

      }

      if (errors[i].length > 0) {
        return promises[i].reject(queryError(errors[i], data));
      }

      promises[i].resolve(data);
    });
  }, function (error) {
    return promises.forEach(function (_ref2) {
      var reject = _ref2.reject;
      return reject(error);
    });
  });
};
var runGroups = function runGroups(runQuery, groups) {
  return groups.reduce(function (p, group) {
    return p.then(function () {
      return runGroup(runQuery, group);
    });
  }, resolved);
};
var createClient = function createClient(_ref3) {
  var runQuery = _ref3.runQuery,
      _ref3$debounce = _ref3.debounce,
      debounce = _ref3$debounce === void 0 ? 50 : _ref3$debounce;
  var next = resolved;
  var timer;
  var pending = [];

  var fire = function fire() {
    // TODO: Control how this chaining is done?
    next = next.then(function () {
      var r = runGroups(runQuery, pending); // Clear now that we fired off stuff

      pending = [];
      timer = null;
      return r;
    });
  };

  return function (query, parameters, // eslint-disable-next-line no-unused-vars
  options) {

    return new Promise(function (resolve, reject) {
      enqueue(pending, createBundle(query), parameters, resolve, reject);

      if (!timer) {
        timer = setTimeout(fire, debounce);
      }
    });
  };
};

export { createClient, enqueue, groupErrors, handleFetchResponse, resolved, runGroup, runGroups };
//# sourceMappingURL=client.js.map
